import { useEffect, useState } from 'react';
import { MobileMetricCard, MobileSectionHeader } from '@/components/base/MobileUi';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';
import { useStore } from '@/hooks/useStore';
import { useAuth } from '@/hooks/useAuth';
import { getFarmerKyc, getFarmersByHtx, getLimitsByFarmer } from '@/store';
import { formatCurrencyCompact } from '@/utils/format';

/** Limit-granting/locking is an Agrida-internal action (CO/OPS/ADMIN/DEPT_HEAD via
 * the contract grant-limit endpoint, not this app — see task 27 notes). HTX_STAFF can
 * only view each farmer's current limit, which the backend already computes from
 * their real Loan/Limit records. */
export default function FarmerLimitTab({ initialFarmerId }: { initialFarmerId?: string }) {
  useStore();
  const { user } = useAuth();
  const farmers = getFarmersByHtx(user?.htxId);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'good' | 'attention'>('all');
  const [focusFarmerId, setFocusFarmerId] = useState(initialFarmerId || '');
  const [expandedId, setExpandedId] = useState('');

  useEffect(() => { if (initialFarmerId) setFocusFarmerId(initialFarmerId); }, [initialFarmerId]);

  const filteredFarmers = (() => {
    const query = search.trim().toLowerCase();
    return farmers.filter((farmer) => {
      if (focusFarmerId && farmer.id !== focusFarmerId) return false;
      if (query && !`${farmer.name} ${farmer.code}`.toLowerCase().includes(query)) return false;
      const kyc = getFarmerKyc(farmer);
      if (filter === 'good') return kyc.riskLevel === 'Tốt' || kyc.riskLevel === 'Khá';
      if (filter === 'attention') return ['Cần chú ý', 'Rủi ro cao', 'Nguy cơ'].includes(kyc.riskLevel || '');
      return true;
    });
  })();

  const totalLimit = farmers.reduce((sum, f) => sum + getLimitsByFarmer(f.id).reduce((s, l) => s + l.hanMuc, 0), 0);
  const totalUsed = farmers.reduce((sum, f) => sum + getLimitsByFarmer(f.id).reduce((s, l) => s + l.daSuDung, 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MobileMetricCard label="Tổng hạn mức" value={formatCurrencyCompact(totalLimit)} caption="Tổng các nông hộ" tone="sky" icon="ri-wallet-3-line" />
        <MobileMetricCard label="Đã sử dụng" value={formatCurrencyCompact(totalUsed)} caption="Đang vay + đang khóa" tone="amber" icon="ri-lock-line" />
        <MobileMetricCard label="Còn lại" value={formatCurrencyCompact(Math.max(0, totalLimit - totalUsed))} caption="Khả dụng" tone="emerald" icon="ri-hand-coin-line" />
        <MobileMetricCard label="Nông hộ" value={farmers.length} caption="Tài khoản đang quản lý" tone="slate" icon="ri-group-line" />
      </div>

      <SearchFilterBar
        value={search}
        onChange={setSearch}
        placeholder="Tìm nông hộ"
        selected={filter === 'all' ? '' : filter}
        onSelect={(value) => setFilter((value || 'all') as typeof filter)}
        options={[
          { value: '', label: 'Tất cả', count: farmers.length },
          { value: 'good', label: 'Tốt', dotClass: 'bg-agrida-500' },
          { value: 'attention', label: 'Cần chú ý', dotClass: 'bg-amber-500' },
        ]}
      >
        {focusFarmerId && <button type="button" onClick={() => setFocusFarmerId('')} className="min-h-11 w-full rounded-xl bg-rose-50 px-3 text-left text-sm font-medium text-rose-600">Bỏ chọn nông hộ hiện tại</button>}
      </SearchFilterBar>

      <section>
        <MobileSectionHeader title="Hạn mức nông hộ" count={`${filteredFarmers.length} nông hộ`} />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredFarmers.map((farmer) => {
            const limits = getLimitsByFarmer(farmer.id);
            const hanMuc = limits.reduce((s, l) => s + l.hanMuc, 0);
            // daSuDung is used+locked combined (see Limit type's doc comment) —
            // conLai still needs the combined figure, but Feedback 08 A1 means
            // the two parts must be shown separately, not both labeled "Đã dùng".
            const daSuDung = limits.reduce((s, l) => s + l.daSuDung, 0);
            const daSuDungThucTe = limits.reduce((s, l) => s + (l.daSuDungThucTe ?? l.daSuDung), 0);
            const dangKhoa = limits.reduce((s, l) => s + (l.dangKhoa ?? 0), 0);
            const conLai = Math.max(0, hanMuc - daSuDung);
            const kyc = getFarmerKyc(farmer);
            const percent = hanMuc > 0 ? Math.min(100, (daSuDung / hanMuc) * 100) : 0;
            const expanded = expandedId === farmer.id;
            return (
              <article key={farmer.id} className="mobile-card min-w-0 p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><h4 className="truncate text-sm font-bold text-slate-900">{farmer.name}</h4><p className="metric-caption mt-0.5 text-[10px] text-slate-400">{farmer.code} · eKYC {kyc.kycStatus === 'ekyc_verified' ? 'đã xác thực' : 'chưa hoàn tất'}</p></div>
                  <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${riskClass(kyc.riskLevel)}`}>{kyc.riskLevel || 'Chưa đánh giá'}</span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <MiniMetric label="Hạn mức" value={formatCurrencyCompact(hanMuc)} />
                  <MiniMetric label="Đã dùng" value={formatCurrencyCompact(daSuDungThucTe)} tone="amber" />
                  <MiniMetric label="Còn lại" value={formatCurrencyCompact(conLai)} tone="emerald" />
                </div>
                {dangKhoa > 0 && <p className="metric-caption mt-1.5 text-[10px] text-slate-400">Đang khóa (chờ giải ngân): {formatCurrencyCompact(dangKhoa)}</p>}
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-agrida-500" style={{ width: `${percent}%` }} /></div>
                <p className="metric-caption mt-1 text-[10px] text-slate-400">Đã sử dụng {percent.toFixed(1)}% hạn mức</p>

                {expanded && (
                  <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
                    <div className="grid grid-cols-2 gap-3">
                      <Detail label="Điểm tín dụng" value={kyc.creditScore ? String(kyc.creditScore) : 'Chưa có'} />
                      <Detail label="Trạng thái" value={conLai > 0 ? 'Còn khả dụng' : 'Đã dùng hết'} />
                    </div>
                  </div>
                )}

                <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                  <button type="button" onClick={() => setExpandedId(expanded ? '' : farmer.id)} className="min-h-11 flex-1 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500">{expanded ? 'Thu gọn' : 'Xem chi tiết'}</button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function MiniMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'amber' | 'emerald' }) {
  const colors = { slate: 'text-slate-800', amber: 'text-amber-700', emerald: 'text-agrida-700' };
  return <div className="min-w-0 rounded-xl bg-slate-50 p-2.5 text-center"><p className="text-[10px] text-slate-400">{label}</p><p className={`mt-0.5 truncate text-xs font-bold ${colors[tone]}`}>{value}</p></div>;
}

function Detail({ label, value }: { label: string; value: string }) { return <div className="min-w-0"><p className="text-[10px] text-slate-400">{label}</p><p className="mt-0.5 text-xs font-semibold text-slate-700 text-safe-normal">{value}</p></div>; }

function riskClass(level?: string) {
  if (level === 'Tốt' || level === 'Khá') return 'bg-agrida-100 text-agrida-700';
  if (level === 'Cần chú ý') return 'bg-amber-100 text-amber-700';
  if (level === 'Rủi ro cao' || level === 'Nguy cơ') return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-600';
}
